// 📄 pages/Proyectos/components/MesaAyudaSection.tsx
// Mesa de ayuda del proyecto (docs/plans/plan-tickets-proyecto.md, T2):
// tickets del día a día del cliente ("no veo una OC", "mi factura fue
// rechazada"). NO son Incidentes (esos son de la implementación).
//   - El cliente crea tickets y ve todos los de su empresa; las respuestas
//     del equipo le llegan como "Equipo de soporte" (no sabe quién atiende).
//   - El equipo ve todo, responde y deja notas internas.
//   - Asignan y cambian el estado: rol Soporte, PM, creador del proyecto y
//     admins de la plataforma (lo valida el backend; acá solo se ocultan
//     los controles).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LifeBuoy, Plus, RefreshCw, X, Send, Lock, MessageSquare, User as UserIcon, AlertCircle } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import {
  TicketProyecto, MensajeTicketProyecto, EstadoTicket, PrioridadTicket, ESTADOS_TICKET, PRIORIDADES_TICKET, ESTADO_TICKET_BADGE, PRIORIDAD_TICKET_COLOR,
  escucharTicketsProyecto, escucharMensajesTicket, crearTicketProyecto, enviarMensajeTicketProyecto, actualizarTicketProyecto, escalarTicketProyecto
} from '../../../services/mesaAyudaService.ts';
import { ReporteExtension, prefillDesdeReporte } from './ReporteExtension.tsx';
import { nombreDocumentoReporte } from '../../../services/knownErrorsService.ts';
import KnownErrorFormModal, { KnownErrorPrefill } from '../../Admin/components/KnownErrorFormModal.tsx';
import IncidenteFormModal from './IncidenteFormModal.tsx';
import type { IncidenteFormData } from '../../../services/incidenteService.ts';
import { clasificacionDe } from '../modulosConfig.ts';

const ESTADO_BADGE = ESTADO_TICKET_BADGE;
const PRIORIDAD_COLOR = PRIORIDAD_TICKET_COLOR;
const ACTIVOS: EstadoTicket[] = ['Abierto', 'En revisión', 'Esperando al cliente'];
const ROLES_GESTION = ['soporte', 'PM'];

const fecha = (ts: any) => (ts?.toDate ? ts.toDate().toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '');

// "Escalar a incidente" (T4 de plan-tickets-proyecto.md): lo que ya se sabe del
// ticket precarga el formulario de incidentes; el equipo revisa antes de guardar.
const prefillIncidente = (ticket: TicketProyecto, sap: boolean, soluciones: string[]): Partial<IncidenteFormData> => {
  const { reporte } = ticket;
  const lineas = [ticket.descripcion, '', `Escalado desde el ticket ${ticket.numero}, abierto por ${ticket.creadoPorNombre || ticket.creadoPorEmail}.`];
  if (reporte) {
    lineas.push(`${nombreDocumentoReporte(reporte.documento)} · ${reporte.documento?.estatus || 'sin estatus'} · ambiente ${reporte.ambiente}`);
    if (reporte.coincidencias?.length) lineas.push(`Errores conocidos: ${reporte.coincidencias.map((c) => c.code).join(', ')}`);
    const eventos = (reporte.eventos || []).filter((e) => e.codigo).slice(0, 5);
    if (eventos.length) lineas.push('Eventos:', ...eventos.map((e) => `- ${e.codigo}: ${e.texto.slice(0, 200)}`));
  }
  return {
    titulo: `${ticket.numero}: ${ticket.titulo}`.slice(0, 150),
    descripcion: lineas.join('\n'),
    fase: 'Soporte',
    prioridad: ticket.prioridad,
    // Los tickets son de la operación: salvo que el reporte diga que es de test, PRD.
    ambiente: reporte?.ambiente === 'test' ? 'QAS' : 'PRD',
    asignadoA: ticket.responsableUid || '',
    reportadoPorNombre: ticket.creadoPorNombre,
    reportadoPorEmail: ticket.creadoPorEmail,
    ...(sap && reporte && soluciones.includes('commerce') ? { solucionRelacionada: 'commerce' } : {}),
    ticketOrigenId: ticket.id,
    ticketOrigenNumero: ticket.numero
  };
};

export default function MesaAyudaSection() {
  const { project, projectId, esCliente, miRol } = useProject();
  const { user, profile } = useAuth();
  const [tickets, setTickets] = useState<TicketProyecto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<'activos' | 'todos' | EstadoTicket>('activos');
  const [soloMios, setSoloMios] = useState(false);
  const [formAbierto, setFormAbierto] = useState(false);
  const [seleccionadoId, setSeleccionadoId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const esAdminPlataforma = profile?.role === 'admin';
  const gestiona = !esCliente && (ROLES_GESTION.includes(miRol || '') || project?.creadoPor === user?.uid || esAdminPlataforma);
  const activa = !!project?.recibeTickets;

  useEffect(() => {
    if (!projectId) return;
    return escucharTicketsProyecto(projectId, setTickets, (err) => {
      console.error('Error cargando la mesa de ayuda:', err);
      setError('No se pudieron cargar los tickets.');
    });
  }, [projectId]);

  // Enlace directo desde los correos (?ticket=...).
  useEffect(() => {
    const id = searchParams.get('ticket');
    if (!id) return;
    setSeleccionadoId(id);
    setSearchParams((prev) => { prev.delete('ticket'); return prev; }, { replace: true });
  }, [searchParams, setSearchParams]);

  const visibles = useMemo(
    () =>
      (tickets || []).filter((t) => {
        if (filtroEstado === 'activos' && !ACTIVOS.includes(t.estado)) return false;
        if (filtroEstado !== 'activos' && filtroEstado !== 'todos' && t.estado !== filtroEstado) return false;
        if (soloMios && t.responsableUid !== user?.uid) return false;
        return true;
      }),
    [tickets, filtroEstado, soloMios, user?.uid]
  );
  const seleccionado = (tickets || []).find((t) => t.id === seleccionadoId) || null;

  if (!project) return null;

  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-gray-400 flex gap-3">
        <LifeBuoy className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <p>
          {esCliente
            ? '¿Algo no funciona en el día a día (no ves una orden de compra, una factura fue rechazada)? Abre un ticket y el equipo de soporte del proyecto te responde aquí.'
            : 'Tickets del día a día del cliente, después de la implementación. Los problemas de la implementación van en Incidentes; si un ticket resulta ser un defecto, se escala a incidente.'}
        </p>
      </div>

      {!activa && (
        <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl p-4 text-sm">
          {esCliente
            ? 'Este proyecto no está recibiendo tickets nuevos. Los que ya existen se siguen atendiendo aquí; para abrir uno nuevo, contacta a tu consultor.'
            : 'La mesa de ayuda está apagada: no se pueden crear tickets nuevos, pero los existentes se siguen atendiendo. Para reactivarla, marca "Recibe tickets de soporte" en Editar proyecto.'}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as typeof filtroEstado)}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="activos">Activos</option>
            <option value="todos">Todos los estados</option>
            {ESTADOS_TICKET.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {!esCliente && (
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={soloMios} onChange={(e) => setSoloMios(e.target.checked)} /> Asignados a mí
            </label>
          )}
        </div>
        {activa && (
          <button
            onClick={() => setFormAbierto(true)}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
          >
            <Plus className="w-4 h-4" /> Crear ticket
          </button>
        )}
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}
      {tickets === null && !error ? (
        <div className="p-8 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-blue-500" /></div>
      ) : visibles.length === 0 ? (
        <p className="text-sm text-gray-500">{(tickets || []).length ? 'Ningún ticket con estos filtros.' : 'Todavía no hay tickets.'}</p>
      ) : (
        <div className="space-y-2">
          {visibles.map((t) => {
            // La otra parte escribió último: hay algo que leer.
            const pendiente = !!t.ultimoMensajePor && t.ultimoMensajePor !== (esCliente ? 'cliente' : 'equipo');
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSeleccionadoId(t.id)}
                className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 space-y-1.5 transition-all"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-gray-500">{t.numero}</span>
                  <span className="font-medium text-white">{t.titulo}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ESTADO_BADGE[t.estado]}`}>{t.estado}</span>
                  <span className={`text-[11px] ${PRIORIDAD_COLOR[t.prioridad]}`}>{t.prioridad}</span>
                  {t.origen === 'extension' && <span className="text-[11px] text-cyan-300">{t.reporte?.plataforma === 'CIG' ? 'Managed Gateway (CIG)' : 'Business Network'}</span>}
                  {t.incidenteId && <span className="text-[11px] text-red-300">Escalado a incidente</span>}
                  {pendiente && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20">
                      {esCliente ? 'Soporte respondió' : 'El cliente respondió'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {t.creadoPorNombre || t.creadoPorEmail} · {fecha(t.fechaCreacion)}
                  {!esCliente && <> · {t.responsableNombre ? `Asignado a ${t.responsableNombre}` : <span className="text-amber-300">Sin asignar</span>}</>}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {formAbierto && (
        <CrearTicketModal
          projectId={projectId}
          onClose={() => setFormAbierto(false)}
          onCreado={(id) => { setFormAbierto(false); setSeleccionadoId(id); }}
        />
      )}
      {seleccionado && (
        <DetalleTicket
          ticket={seleccionado}
          projectId={projectId}
          esCliente={esCliente}
          gestiona={gestiona}
          esAdminPlataforma={esAdminPlataforma}
          equipo={(project.miembros || []).filter((m) => m.rol !== 'cliente' && m.activo !== false)}
          onClose={() => setSeleccionadoId(null)}
        />
      )}
    </div>
  );
}

function CrearTicketModal({ projectId, onClose, onCreado }: { projectId: string; onClose: () => void; onCreado: (id: string) => void }) {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [prioridad, setPrioridad] = useState<PrioridadTicket>('Media');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      const { ticketId } = await crearTicketProyecto(projectId, { titulo: titulo.trim(), descripcion: descripcion.trim(), prioridad });
      onCreado(ticketId);
    } catch (err: any) {
      setError(err.message || 'No se pudo crear el ticket.');
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = 'w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500';
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">Nuevo ticket</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={enviar} className="p-5 space-y-4 overflow-y-auto">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Título*</label>
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={150} placeholder="Ej.: No puedo ver la OC 4500002029" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Prioridad</label>
            <select value={prioridad} onChange={(e) => setPrioridad(e.target.value as PrioridadTicket)} className={inputClass}>
              {PRIORIDADES_TICKET.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Descripción*</label>
            <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={5} maxLength={4000} placeholder="Qué pasó, qué intentaste y desde cuándo." className={inputClass} />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancelar</button>
            <button
              type="submit"
              disabled={guardando || !titulo.trim() || !descripcion.trim()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear ticket
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DetalleTicket({
  ticket, projectId, esCliente, gestiona, esAdminPlataforma, equipo, onClose
}: {
  ticket: TicketProyecto; projectId: string; esCliente: boolean; gestiona: boolean; esAdminPlataforma: boolean;
  equipo: { userId: string; email: string; rol: string }[]; onClose: () => void;
}) {
  const [mensajes, setMensajes] = useState<MensajeTicketProyecto[] | null>(null);
  const [texto, setTexto] = useState('');
  const [interno, setInterno] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<KnownErrorPrefill | null>(null);
  const [escalando, setEscalando] = useState(false);
  const { project, esAdminObservador } = useProject();
  const { modoArea, opciones: solucionesActivas } = clasificacionDe(project);
  const finRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMensajes(null);
    return escucharMensajesTicket(projectId, ticket.id, esCliente, setMensajes, (err) => {
      console.error('Error leyendo la conversación:', err);
      setError('No se pudo cargar la conversación.');
    });
  }, [projectId, ticket.id, esCliente]);

  useEffect(() => { finRef.current?.scrollIntoView({ block: 'nearest' }); }, [mensajes]);

  const cerrado = ticket.estado === 'Cerrado';
  // Un admin de la plataforma que solo observa no crea incidentes (rules).
  const puedeEscalar = gestiona && !esAdminObservador && !cerrado && !ticket.incidenteId;

  const enlazarIncidente = async (incidenteId: string) => {
    try {
      await escalarTicketProyecto(projectId, ticket.id, incidenteId);
    } catch (err: any) {
      setError(`El incidente se creó, pero no se pudo enlazar con el ticket: ${err.message || 'intenta de nuevo.'} Revísalo en Incidentes antes de volver a escalar.`);
    }
  };

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!texto.trim() || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await enviarMensajeTicketProyecto(projectId, ticket.id, texto.trim(), interno);
      setTexto('');
    } catch (err: any) {
      setError(err.message || 'No se pudo enviar el mensaje.');
    } finally {
      setEnviando(false);
    }
  };

  const actualizar = async (cambios: { estado?: EstadoTicket; responsableUid?: string | null }) => {
    setError(null);
    try {
      await actualizarTicketProyecto(projectId, ticket.id, cambios);
    } catch (err: any) {
      setError(err.message || 'No se pudo actualizar el ticket.');
    }
  };

  const selectClass = 'bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-xs text-gray-500 font-mono">{ticket.numero}</p>
            <h3 className="text-lg font-medium text-white break-words">{ticket.titulo}</h3>
            <p className="text-xs text-gray-500 mt-1">
              {ticket.creadoPorNombre || ticket.creadoPorEmail} · {fecha(ticket.fechaCreacion)} · prioridad <span className={PRIORIDAD_COLOR[ticket.prioridad]}>{ticket.prioridad}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex items-center gap-3 flex-wrap">
            {gestiona ? (
              <>
                <select value={ticket.estado} onChange={(e) => actualizar({ estado: e.target.value as EstadoTicket })} className={selectClass}>
                  {ESTADOS_TICKET.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                  value={ticket.responsableUid || ''}
                  onChange={(e) => actualizar({ responsableUid: e.target.value || null })}
                  className={selectClass}
                >
                  <option value="">Sin asignar</option>
                  {equipo.map((m) => <option key={m.userId} value={m.userId}>{m.email} · {m.rol}</option>)}
                </select>
              </>
            ) : (
              <>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_BADGE[ticket.estado]}`}>{ticket.estado}</span>
                {!esCliente && <span className="text-xs text-gray-400">{ticket.responsableNombre ? `Asignado a ${ticket.responsableNombre}` : 'Sin asignar'}</span>}
              </>
            )}
            {puedeEscalar && (
              <button
                type="button"
                onClick={() => setEscalando(true)}
                className="flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200 border border-red-500/30 rounded-lg px-2 py-1.5"
              >
                <AlertCircle className="w-3.5 h-3.5" /> Escalar a incidente
              </button>
            )}
          </div>

          {ticket.incidenteId && (
            <Link to={`/proyecto/${projectId}/incidentes?incidenteId=${ticket.incidenteId}`} className="flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200">
              <AlertCircle className="w-3.5 h-3.5" /> Escalado a incidente: {ticket.incidenteTitulo || ticket.incidenteId}
            </Link>
          )}

          <p className="text-sm text-gray-300 whitespace-pre-wrap">{ticket.descripcion}</p>

          {ticket.reporte && (
            <ReporteExtension
              reporte={ticket.reporte}
              onConvertir={esAdminPlataforma ? () => setPrefill(prefillDesdeReporte(ticket.reporte!)) : undefined}
            />
          )}

          <div className="border-t border-white/10 pt-4 space-y-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Conversación
            </p>
            {mensajes === null && !error ? (
              <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
            ) : (mensajes || []).length === 0 ? (
              <p className="text-xs text-gray-500">Todavía no hay mensajes.</p>
            ) : (
              <div className="space-y-2">
                {(mensajes || []).map((m) => {
                  // Del lado del cliente, todo lo del equipo es "Equipo de soporte".
                  const mio = esCliente ? m.autorLado === 'cliente' : m.autorLado === 'equipo';
                  const autor = esCliente && m.autorLado === 'equipo' ? 'Equipo de soporte' : m.autorNombre || (m.autorLado === 'equipo' ? 'Equipo' : 'Cliente');
                  return (
                    <div key={m.id} className={`flex ${mio ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm border ${
                          m.interno ? 'bg-amber-500/10 border-amber-500/30' : mio ? 'bg-blue-600/20 border-blue-500/30' : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <p className="text-[11px] text-gray-400 mb-0.5 flex items-center gap-1">
                          {m.interno && <Lock className="w-3 h-3" />}
                          {m.interno ? 'Nota interna · ' : ''}{autor} · {fecha(m.creadoEn)}
                        </p>
                        <p className="text-gray-200 whitespace-pre-wrap break-words">{m.texto}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={finRef} />
              </div>
            )}

            {error && <p className="text-xs text-red-400">{error}</p>}

            {cerrado ? (
              <p className="text-xs text-gray-500">El ticket está cerrado: no admite mensajes nuevos.</p>
            ) : (
              <form onSubmit={enviar} className="space-y-2">
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={esCliente ? 'Escribe al equipo de soporte…' : interno ? 'Nota interna (el cliente no la ve)…' : 'Responde al cliente…'}
                  className={`w-full bg-zinc-800 border rounded-lg p-2 text-sm text-white focus:outline-none ${interno ? 'border-amber-500/40 focus:border-amber-400' : 'border-white/10 focus:border-blue-500'}`}
                />
                <div className="flex items-center justify-between gap-3">
                  {!esCliente ? (
                    <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                      <input type="checkbox" checked={interno} onChange={(e) => setInterno(e.target.checked)} />
                      <Lock className="w-3.5 h-3.5" /> Nota interna
                    </label>
                  ) : <span />}
                  <button
                    type="submit"
                    disabled={enviando || !texto.trim()}
                    className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-2 rounded-lg text-sm font-semibold text-white"
                  >
                    {enviando ? <RefreshCw className="w-4 h-4 animate-spin" /> : interno ? <UserIcon className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    {interno ? 'Guardar nota' : 'Enviar'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {prefill && (
        <KnownErrorFormModal defaultModule="ariba" initialData={prefill} onClose={() => setPrefill(null)} onSaved={() => setPrefill(null)} />
      )}
      {escalando && project && (
        <IncidenteFormModal
          projectId={projectId}
          solucionesActivas={solucionesActivas}
          modoArea={modoArea}
          miembros={project.miembros}
          prefill={prefillIncidente(ticket, !modoArea, solucionesActivas)}
          onCreated={enlazarIncidente}
          onClose={() => setEscalando(false)}
          onSaved={() => setEscalando(false)}
        />
      )}
    </div>
  );
}
